import React, { useState, useEffect, useCallback } from 'react';
import usuarioService from '../../services/usuario.service';
import BtnIcone from '../../components/BtnIcone';
import Modal from '../../components/Modal';
import SePode from '../../components/SePode';   // r88: botões condicionais por permissão
import BrowseHeader from '../../components/BrowseHeader';                    // r106
import { iconeDaRotina } from '../../constants/iconesRotinas';               // r106
import { useAuth } from '../../context/AuthContext';                         // r128
import { usePermissoes } from '../../context/PermissoesContext';              // r159

// ─── Paleta de cores ──────────────────────────────────────────────────────────
const COR = {
  azul:    '#003399',
  azulBg:  '#eff6ff',
  bg:      '#f0f2f7',
  branco:  '#fff',
  borda:   '#e8eaed',
  texto:   '#1a1a2e',
  muted:   '#888',
  verde:   '#1D9E75',
  verdeBg: '#f0fff4',
  vermelho:'#e53935',
  vermBg:  '#fff0f0',
  amarelo: '#d97706',
  amlBg:   '#fffbeb',
};

// ─── Status pill ──────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const mapa = {
    A: { label: 'Ativo',     bg: COR.verdeBg, cor: COR.verde   },
    B: { label: 'Bloqueado', bg: COR.vermBg,  cor: COR.vermelho },
    I: { label: 'Inativo',   bg: COR.amlBg,   cor: COR.amarelo },
  };
  const s = mapa[status] || mapa.I;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '500',
      backgroundColor: s.bg, color: s.cor,
      border: `1px solid ${s.cor}`,           // r108: borda na cor da fonte
    }}>{s.label}</span>
  );
}

// ─── Botão de ação do browse ──────────────────────────────────────────────────


// ─── Campo do formulário ──────────────────────────────────────────────────────
function Campo({ label, children, obrigatorio }) {
  return (
    // r112: item 5 — espaçamento entre campos: 14 → 28px (mais 100%)
    <div style={{ marginBottom: '28px' }}>
      {/* r112: item 4 — label mais próxima do campo: 5 → 2px */}
      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: COR.muted, marginBottom: '2px' }}>
        {label}{obrigatorio && <span style={{ color: COR.vermelho }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
  // r112: item 1 — fonte padrão Manrope (selects nativos não herdam por default)
  fontFamily: "'Manrope', sans-serif",
};

const selectStyle = { ...inputStyle, cursor: 'pointer' };

// r112: item 3 — máscara CPF (000.000.000-00)
function formatarCpf(valor) {
  const apenasNum = String(valor || '').replace(/\D/g, '').slice(0, 11);
  const len = apenasNum.length;
  if (len <= 3)  return apenasNum;
  if (len <= 6)  return `${apenasNum.slice(0, 3)}.${apenasNum.slice(3)}`;
  if (len <= 9)  return `${apenasNum.slice(0, 3)}.${apenasNum.slice(3, 6)}.${apenasNum.slice(6)}`;
  return `${apenasNum.slice(0, 3)}.${apenasNum.slice(3, 6)}.${apenasNum.slice(6, 9)}-${apenasNum.slice(9, 11)}`;
}
function limparCpf(valor) {
  return String(valor || '').replace(/\D/g, '').slice(0, 11);
}

// ─── r115: Foto do usuário ────────────────────────────────────────────────────
// Helper: monta URL absoluta da foto. O backend retorna caminho relativo
// (/uploads/usuarios/...). 
// r125: monta a URL usando o MESMO host do navegador (window.location.hostname)
// — a foto é servida pelo mesmo domínio (caminho relativo via reverse-proxy).
function urlDaFoto(caminho) {
  if (!caminho) return '';
  if (caminho.startsWith('http')) return caminho;
  const base = process.env.REACT_APP_API_BASE ? process.env.REACT_APP_API_BASE.replace(/\/api$/, '') : '';
  return `${base}${caminho}`;
}

// Componente: avatar circular grande + botões Escolher/Remover
// r116: ícones SVG (substituem emojis 📷, ⏳, ✕)
const SvgCamera = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);
const SvgRelogio = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);
const SvgX = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
// r117: ícones de zoom
const SvgMinus = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const SvgPlus = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

// r116: Crop circular arrastável.
//   - Preview num círculo 180×180
//   - Arrastar move a imagem dentro do círculo (ajuste de enquadramento)
//   - Ao salvar, o canvas faz crop respeitando o offset (handled em salvar())
//
// Tamanho do preview = 180px. O offset em px é relativo a esse container.
const PREVIEW_SIZE = 120;        // r118: era 180
const AVATAR_FINAL_SIZE = 400;   // tamanho do PNG final salvo (qualidade boa)

function FotoUsuario({
  fotoUrl, fotoPendente, nomeAmigavel,
  onEscolher, onRemover,
  ajuste, onAjuste,          // r116: state controlado de fora ({x, y})
  zoom, onZoom,              // r117: zoom controlado (1 a 3)
  somenteLeitura,
}) {
  // Preview do File pendente (em memória, antes do upload)
  const [previewLocal, setPreviewLocal] = React.useState('');
  React.useEffect(() => {
    if (!fotoPendente) { setPreviewLocal(''); return; }
    const url = URL.createObjectURL(fotoPendente);
    setPreviewLocal(url);
    return () => URL.revokeObjectURL(url);
  }, [fotoPendente]);

  // Iniciais — fallback quando não tem foto nem pendente
  const iniciais = (nomeAmigavel || 'U')
    .split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();

  const imagem = previewLocal || urlDaFoto(fotoUrl);
  const temAlguma = !!imagem;
  // Só é arrastável se for foto pendente (a foto já salva no banco já vem cortada)
  const podeArrastar = !somenteLeitura && !!fotoPendente;

  const inputId = 'foto-usuario-input';

  // ── Drag state ──────────────────────────────────────────────────────────
  const arrastando = React.useRef(false);
  const origem    = React.useRef({ x: 0, y: 0 });
  const ajusteInicial = React.useRef({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (!podeArrastar) return;
    e.preventDefault();
    arrastando.current = true;
    origem.current = { x: e.clientX, y: e.clientY };
    ajusteInicial.current = { ...ajuste };
  };
  React.useEffect(() => {
    const move = (e) => {
      if (!arrastando.current) return;
      const dx = e.clientX - origem.current.x;
      const dy = e.clientY - origem.current.y;
      onAjuste({
        x: ajusteInicial.current.x + dx,
        y: ajusteInicial.current.y + dy,
      });
    };
    const up = () => { arrastando.current = false; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup',   up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup',   up);
    };
  }, [onAjuste]);

  return (
    // r117 item 1: alinhamento à esquerda (era center)
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
      gap: '14px',
      marginTop: '24px', paddingTop: '20px',
      borderTop: `1px solid ${COR.borda}`,
    }}>
      {/* Título da seção */}
      <div style={{
        fontSize: '11px', fontWeight: '700', color: COR.azul,
        textTransform: 'uppercase', letterSpacing: '0.6px',
      }}>
        Foto do usuário
      </div>

      {/* Avatar circular grande (180px) */}
      <div
        onMouseDown={onMouseDown}
        style={{
          width:  `${PREVIEW_SIZE}px`,
          height: `${PREVIEW_SIZE}px`,
          borderRadius: '50%',
          backgroundColor: COR.azulBg,
          border: `2px solid ${COR.azul}`,
          color: COR.azul, fontSize: '38px', fontWeight: '700',
          overflow: 'hidden', flexShrink: 0,
          position: 'relative',
          cursor: podeArrastar ? (arrastando.current ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {temAlguma ? (
          <img
            src={imagem}
            alt="foto"
            draggable={false}
            style={{
              // r118: 'contain' faz a foto inteira caber no círculo em zoom 1×.
              // Aumentando o zoom, ela cresce (vai cobrindo o círculo).
              maxWidth: '100%', maxHeight: '100%',
              objectFit: 'contain',
              position: 'absolute',
              left: '50%', top: '50%',
              transform: `translate(calc(-50% + ${ajuste.x}px), calc(-50% + ${ajuste.y}px)) scale(${zoom})`,
              pointerEvents: 'none',
            }}
          />
        ) : iniciais}
      </div>

      {/* Dica de uso do crop */}
      {podeArrastar && (
        <div style={{ fontSize: '11px', color: COR.muted }}>
          Aumente o zoom até preencher o círculo, depois arraste para reposicionar
        </div>
      )}

      {/* r117: Controle de zoom (apenas pra foto pendente) */}
      {podeArrastar && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          width: '260px',   // r118: largura fixa pro slider ficar confortável (não mais amarrado ao PREVIEW_SIZE)
        }}>
          <button
            type="button"
            onClick={() => onZoom(Math.max(1, +(zoom - 0.1).toFixed(2)))}
            disabled={zoom <= 1}
            title="Diminuir zoom"
            style={{
              width: '28px', height: '28px', flexShrink: 0,
              border: `1px solid ${COR.borda}`, borderRadius: '5px',
              backgroundColor: COR.branco, color: COR.azul,
              cursor: zoom <= 1 ? 'not-allowed' : 'pointer',
              opacity: zoom <= 1 ? 0.4 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0,
            }}
          >
            <SvgMinus />
          </button>

          <input
            type="range"
            min="1" max="3" step="0.01"
            value={zoom}
            onChange={e => onZoom(parseFloat(e.target.value))}
            style={{
              flex: 1, accentColor: COR.azul, cursor: 'pointer',
            }}
          />

          <button
            type="button"
            onClick={() => onZoom(Math.min(3, +(zoom + 0.1).toFixed(2)))}
            disabled={zoom >= 3}
            title="Aumentar zoom"
            style={{
              width: '28px', height: '28px', flexShrink: 0,
              border: `1px solid ${COR.borda}`, borderRadius: '5px',
              backgroundColor: COR.branco, color: COR.azul,
              cursor: zoom >= 3 ? 'not-allowed' : 'pointer',
              opacity: zoom >= 3 ? 0.4 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0,
            }}
          >
            <SvgPlus />
          </button>

          <span style={{
            fontSize: '11px', color: COR.muted, minWidth: '36px',
            fontVariantNumeric: 'tabular-nums',
          }}>{zoom.toFixed(1)}x</span>
        </div>
      )}

      {/* Linha de descrição + status pendente */}
      <div style={{ fontSize: '12px', color: COR.muted }}>
        JPG, PNG, WEBP ou GIF. Máximo 5 MB.
        {fotoPendente && (
          <span style={{
            color: COR.amarelo, fontWeight: '600', marginLeft: '6px',
            display: 'inline-flex', alignItems: 'center', gap: '3px',
            verticalAlign: 'middle',
          }}>
            <SvgRelogio /> Pendente — sobe ao salvar
          </span>
        )}
      </div>

      {/* Botões */}
      {!somenteLeitura && (
        <div style={{ display: 'flex', gap: '8px' }}>
          <label htmlFor={inputId} style={{
            padding: '7px 14px', fontSize: '12px', fontWeight: '600',
            backgroundColor: COR.azul, color: COR.branco,
            borderRadius: '5px', cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}>
            <SvgCamera />
            {temAlguma ? 'Trocar foto' : 'Escolher foto'}
          </label>
          <input
            id={inputId}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onEscolher}
            style={{ display: 'none' }}
          />
          {temAlguma && (
            <button
              type="button"
              onClick={onRemover}
              style={{
                padding: '7px 14px', fontSize: '12px', fontWeight: '600',
                backgroundColor: 'transparent', color: COR.vermelho,
                border: `1px solid ${COR.vermelho}`,
                borderRadius: '5px', cursor: 'pointer',
                fontFamily: "'Manrope', sans-serif",
                display: 'inline-flex', alignItems: 'center', gap: '6px',
              }}
            >
              <SvgX /> Remover
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── r127: Crop helper com zoom — refatorado usando ctx.translate/scale ──────
// Gera um Blob PNG quadrado AVATAR_FINAL_SIZE x AVATAR_FINAL_SIZE.
//
// Estratégia: replicar o EXATO transform CSS do preview no canvas, mas
// usando ctx.translate + ctx.scale (mais previsível que calcular dimensões
// pré-multiplicadas).
//
// CSS no preview:
//   - img com object-fit: contain dentro de PREVIEW_SIZE x PREVIEW_SIZE
//   - transform: translate(calc(-50% + ajuste.x px), calc(-50% + ajuste.y px)) scale(zoom)
//   - left: 50%, top: 50%
//
// Significado: img é centralizada no preview, depois deslocada por
// ajuste.(x,y) px, depois escalada por zoom em torno do centro do img.
//
// Replicar no canvas:
// 1) Calcula tamanho "natural contain" no preview (sem zoom)
// 2) Define o centro do canvas como origem
// 3) Aplica ajuste (não escalado)
// 4) Aplica scale(zoom)
// 5) Desenha a img centralizada no novo origin
async function gerarFotoCortada(file, ajuste, zoom = 1) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        // 1) Calcula dimensões "contain" NATURAL no preview (sem zoom)
        const ratioImg = img.width / img.height;
        let imgWPrev, imgHPrev;
        if (ratioImg > 1) {
          imgWPrev = PREVIEW_SIZE;
          imgHPrev = PREVIEW_SIZE / ratioImg;
        } else {
          imgHPrev = PREVIEW_SIZE;
          imgWPrev = PREVIEW_SIZE * ratioImg;
        }
        // Escala pra dimensões "final" — o canvas é AVATAR_FINAL_SIZE em vez
        // de PREVIEW_SIZE, então tudo amplifica por essa razão.
        const scale = AVATAR_FINAL_SIZE / PREVIEW_SIZE;
        const imgW = imgWPrev * scale;
        const imgH = imgHPrev * scale;
        const ajusteX = ajuste.x * scale;   // ajuste em px do preview convertido pro canvas
        const ajusteY = ajuste.y * scale;

        // 2) Desenha no canvas usando translate + scale (espelha o transform CSS)
        const canvas = document.createElement('canvas');
        canvas.width  = AVATAR_FINAL_SIZE;
        canvas.height = AVATAR_FINAL_SIZE;
        const ctx = canvas.getContext('2d');

        // Fundo azul-claro (cor do círculo no preview, pra continuidade visual)
        ctx.fillStyle = '#eff6ff';
        ctx.fillRect(0, 0, AVATAR_FINAL_SIZE, AVATAR_FINAL_SIZE);
        ctx.imageSmoothingQuality = 'high';

        // Replica o transform CSS:
        //   1. Centro do canvas = ponto de origem
        //   2. Aplica deslocamento (translate) por ajuste — NÃO multiplicado por zoom
        //      porque o ctx.translate vem ANTES do scale (igual ao CSS,
        //      que executa "right to left": scale primeiro, mas a partir da
        //      coordinate transformada já pelo translate "left")
        //   3. Aplica scale(zoom) em torno do novo origin (centro deslocado)
        //   4. Desenha a img centralizada na nova origin
        //
        // Em CSS `translate(X) scale(S)`, a fórmula efetiva pra cada ponto P é:
        //   P_final = (P - centro_elemento) * S + centro_elemento + (X, 0)
        //
        // O equivalente em canvas (com translate + scale aplicados em ordem):
        //   ctx.translate(canvas/2 + X, canvas/2 + Y);
        //   ctx.scale(S, S);
        //   ctx.drawImage(img, -imgW/2, -imgH/2, imgW, imgH);
        ctx.translate(AVATAR_FINAL_SIZE / 2 + ajusteX, AVATAR_FINAL_SIZE / 2 + ajusteY);
        ctx.scale(zoom, zoom);
        ctx.drawImage(img, -imgW / 2, -imgH / 2, imgW, imgH);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (!blob) return reject(new Error('Falha ao gerar imagem.'));
          const nomeOriginal = file.name.replace(/\.[^.]+$/, '');
          const arquivo = new File([blob], `${nomeOriginal}.png`, { type: 'image/png' });
          resolve(arquivo);
        }, 'image/png', 0.92);
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível carregar a imagem.'));
    };
    img.src = url;
  });
}

// ─── Estado inicial do formulário ─────────────────────────────────────────────
const FORM_VAZIO = {
  nome: '', nome_amigavel: '', email: '', cpf: '',
  nascimento: '', sexo: 'M', grupo_acesso_id: '', status: 'A',
  senha: '', conf_senha: '',
  foto: '',                      // r115: caminho relativo (ex: /uploads/usuarios/xxx.jpg)
};

// r151: normaliza datas vindas do backend (ISO datetime ou Date) pro
//       formato YYYY-MM-DD que o <input type="date"> espera.
function dataParaInput(d) {
  if (!d) return '';
  if (typeof d === 'string') {
    // Já é "YYYY-MM-DD" ou começa com isso → pega os 10 primeiros chars
    // (evita problema de timezone que toISOString causaria)
    return d.slice(0, 10);
  }
  if (d instanceof Date) {
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const dd   = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  return '';
}

// r151: mescla `u` no FORM_VAZIO normalizando campos especiais (data, etc)
function preencherForm(u) {
  return {
    ...FORM_VAZIO,
    ...u,
    nascimento: dataParaInput(u?.nascimento),
  };
}

// ─── Componente principal ─────────────────────────────────────────────────────
function UsuariosPage() {
  // r128: pra atualizar foto do user logado em tempo real (Sidebar refletir imediato)
  const { usuario: usuarioLogado, atualizarUsuario } = useAuth();
  const { isAdmin, isBootstrap, pode } = usePermissoes();                 // r159/r171
  const podeMexerEmCamposAdmin = isAdmin || isBootstrap;                  // r159

  const [usuarios, setUsuarios]     = useState([]);
  const [grupos, setGrupos]         = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca]           = useState('');
  const [ordenacao, setOrdenacao]   = useState({ campo: 'nome', dir: 'asc' });

  // Painel lateral
  const [painel, setPainel]   = useState(null); // null | 'novo' | 'editar' | 'visualizar'
  const [selecionado, setSelecionado] = useState(null);
  const [form, setForm]       = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erroPainel, setErroPainel] = useState('');
  const [sucessoPainel, setSucessoPainel] = useState('');

  // ── Carrega dados ────────────────────────────────────────────────────────────
  // r157: Promise.allSettled — se listarGrupos() falhar por permissão (ex: user
  //       que tem usuarios/visualizar com filtro próprio mas não tem
  //       grupos-acesso/visualizar), a lista de usuários ainda carrega.
  //       Sem isso, o catch antigo substituía TUDO pelo mock fake do admin.
  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      const [resUsuarios, resGrupos] = await Promise.allSettled([
        usuarioService.listar(),
        usuarioService.listarGrupos(),
      ]);
      if (resUsuarios.status === 'fulfilled') {
        setUsuarios(resUsuarios.value || []);
      } else {
        console.warn('Não foi possível listar usuários:', resUsuarios.reason?.message);
        setUsuarios([]);
      }
      if (resGrupos.status === 'fulfilled') {
        setGrupos(resGrupos.value || []);
      } else {
        console.warn('Não foi possível listar grupos:', resGrupos.reason?.message);
        setGrupos([]);
      }
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Filtro e ordenação ───────────────────────────────────────────────────────
  const usuariosFiltrados = usuarios
    .filter(u => {
      const q = busca.toLowerCase();
      return !q || u.nome.toLowerCase().includes(q)
        || u.email.toLowerCase().includes(q)
        || (u.nome_amigavel || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const va = (a[ordenacao.campo] || '').toString().toLowerCase();
      const vb = (b[ordenacao.campo] || '').toString().toLowerCase();
      return ordenacao.dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });

  const toggleOrdem = (campo) => {
    setOrdenacao(o => ({
      campo,
      dir: o.campo === campo && o.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const setaCabecalho = (campo) => {
    if (ordenacao.campo !== campo) return ' ↕';
    return ordenacao.dir === 'asc' ? ' ↑' : ' ↓';
  };

  // ── Ações do painel ──────────────────────────────────────────────────────────
  const abrirNovo = () => {
    setForm(FORM_VAZIO);
    setSelecionado(null);
    setErroPainel('');
    setSucessoPainel('');
    setPainel('novo');
  };

  const abrirVisualizar = (u) => {
    setSelecionado(u);
    setForm(preencherForm(u));
    setErroPainel('');
    setSucessoPainel('');
    setPainel('visualizar');
  };

  const abrirEditar = (u) => {
    setSelecionado(u);
    setForm({ ...preencherForm(u), senha: '', conf_senha: '' });
    setErroPainel('');
    setSucessoPainel('');
    setPainel('editar');
  };

  // r115: foto pendente (File ainda não enviado em modo "novo" — sobe após criar o user)
  const [fotoPendente, setFotoPendente] = useState(null);
  // r116: ajuste de crop (x, y em px relativo ao container 200x200 do preview)
  //       É aplicado no canvas ao gerar o blob final pra upload.
  const [fotoAjuste, setFotoAjuste]     = useState({ x: 0, y: 0 });
  // r117: zoom da foto (1 = tamanho cover natural; até 3x)
  const [fotoZoom, setFotoZoom]         = useState(1);

  const fecharPainel = () => {
    setPainel(null); setSelecionado(null);
    setFotoPendente(null);            // r115: limpa preview pendente ao fechar
    setFotoAjuste({ x: 0, y: 0 });    // r116: reseta crop
    setFotoZoom(1);                   // r117: reseta zoom
  };

  const handleCampo = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  // r115 — Upload de foto
  // No modo "editar"/"novo" mesmo antes de salvar o usuário, guarda o File em
  // fotoPendente e mostra preview local. Só sobe pro servidor depois de salvar
  // (no editar, pode ser imediato; no novo, depois do incluir).
  const escolherFoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErroPainel('Foto muito grande (máx 5 MB).');
      return;
    }
    if (!/^image\//.test(file.type)) {
      setErroPainel('Use uma imagem (JPG, PNG, WEBP ou GIF).');
      return;
    }
    setErroPainel('');
    setFotoPendente(file);
    setFotoAjuste({ x: 0, y: 0 });   // r116: reseta crop ao escolher nova foto
    setFotoZoom(1);                  // r117: reseta zoom ao escolher nova foto
  };

  const removerFoto = async () => {
    if (fotoPendente) {
      setFotoPendente(null);         // só cancela o pendente
      return;
    }
    if (!form.foto || !selecionado?.id) return;
    if (!window.confirm('Remover foto atual?')) return;
    try {
      await usuarioService.removerFoto(selecionado.id);
      setForm(f => ({ ...f, foto: '' }));
      // r128: se for o próprio user logado, limpa foto no AuthContext (Sidebar volta às iniciais)
      if (usuarioLogado?.id === selecionado.id) {
        atualizarUsuario({ foto: null });
      }
      await carregar();
    } catch (err) {
      setErroPainel(err.response?.data?.mensagem || 'Erro ao remover foto.');
    }
  };

  // ── Salvar ───────────────────────────────────────────────────────────────────
  const salvar = async () => {
    setErroPainel('');
    if (!form.nome.trim())           return setErroPainel('Nome é obrigatório.');
    if (!form.nome_amigavel.trim())  return setErroPainel('Nome amigável é obrigatório.');
    if (!form.email.trim())          return setErroPainel('E-mail é obrigatório.');
    if (!form.sexo)                  return setErroPainel('Sexo é obrigatório.');
    if (!form.grupo_acesso_id)       return setErroPainel('Grupo de acesso é obrigatório.');
    if (painel === 'novo') {
      if (!form.senha)               return setErroPainel('Senha é obrigatória.');
      if (form.senha.length < 8)     return setErroPainel('Senha deve ter no mínimo 8 caracteres.');
      if (form.senha !== form.conf_senha) return setErroPainel('As senhas não conferem.');
    }

    try {
      setSalvando(true);
      let usuarioId;
      if (painel === 'novo') {
        const res = await usuarioService.incluir(form);
        // r115: backend deve retornar { id, ... }; se vier { usuario: {...} }, ajusta
        usuarioId = res?.id || res?.usuario?.id;
      } else {
        await usuarioService.alterar(selecionado.id, form);
        usuarioId = selecionado.id;
      }
      // r115/r116/r117: faz upload de foto pendente (cortada + zoom), se houver
      if (fotoPendente && usuarioId) {
        // r118+: trata erros do crop (canvas) e do upload (rede) separados
        //        e mostra a causa real pra debug
        let arquivoFinal;
        try {
          arquivoFinal = await gerarFotoCortada(fotoPendente, fotoAjuste, fotoZoom);
        } catch (errCrop) {
          console.error('[salvar] falha ao processar a imagem:', errCrop);
          setErroPainel(`Usuário salvo, mas a imagem não pôde ser processada: ${errCrop.message || 'erro desconhecido'}`);
          setSalvando(false);
          await carregar();
          return;
        }
        try {
          const resUpload = await usuarioService.uploadFoto(usuarioId, arquivoFinal);
          // r128: se o usuário que está sendo editado é o próprio logado,
          // atualiza a foto no AuthContext imediato (Sidebar atualiza sem F5)
          if (usuarioLogado?.id === usuarioId && resUpload?.foto) {
            atualizarUsuario({ foto: resUpload.foto });
          }
        } catch (errUpload) {
          // Loga o detalhe completo pro devtools
          console.error('[salvar] falha no upload da foto:', {
            usuarioId,
            status:    errUpload.response?.status,
            mensagem:  errUpload.response?.data?.mensagem,
            data:      errUpload.response?.data,
            message:   errUpload.message,
          });
          const detalhe =
            errUpload.response?.data?.mensagem ||
            errUpload.response?.statusText ||
            errUpload.message ||
            'erro de rede';
          const status = errUpload.response?.status ? ` (HTTP ${errUpload.response.status})` : '';
          setErroPainel(`Usuário salvo, mas a foto não foi enviada${status}: ${detalhe}`);
          setSalvando(false);
          await carregar();
          return;
        }
      }
      setFotoPendente(null);
      setSucessoPainel(painel === 'novo' ? 'Usuário incluído com sucesso!' : 'Usuário alterado com sucesso!');
      await carregar();
      setTimeout(fecharPainel, 1200);
    } catch (err) {
      setErroPainel(err.response?.data?.mensagem || 'Erro ao salvar. Verifique os dados.');
    } finally {
      setSalvando(false);
    }
  };

  // ── Bloquear / desbloquear ───────────────────────────────────────────────────
  const alterarStatus = async (u) => {
    const novoStatus = u.status === 'A' ? 'B' : 'A';
    try {
      await usuarioService.alterarStatus(u.id, novoStatus);
      await carregar();
    } catch {
      alert('Erro ao alterar status do usuário.');
    }
  };

  // ── Resetar senha ────────────────────────────────────────────────────────────
  const resetarSenha = async (u) => {
    if (!window.confirm(`Resetar a senha de ${u.nome_amigavel}? Uma nova senha será enviada por e-mail.`)) return;
    try {
      await usuarioService.resetarSenha(u.id);
      alert('Senha resetada e enviada por e-mail.');
    } catch {
      alert('Erro ao resetar senha.');
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  const painelAberto = !!painel;
  const somenteLeitura = painel === 'visualizar';
  // r171: campo "Grupo de acesso" gated por permissão específica (admin/bootstrap sempre).
  //       Novo usuário → 'grupo-incluir'; edição → 'grupo-alterar'.
  const podeDefinirGrupo = podeMexerEmCamposAdmin
    || pode('usuarios', painel === 'novo' ? 'grupo-incluir' : 'grupo-alterar');

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif" }}>

      {/* r106: BrowseHeader padronizado no topo */}
      <BrowseHeader
        rotina="usuarios"
        titulo="Usuários"
        descricao="Cadastro dos usuários com acesso ao sistema."
      />

      {/* ── BROWSE ── */}
      <div style={{
        flex: 1, backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px',
        overflow: 'hidden', minWidth: 0,
      }}>
        {/* Cabeçalho do browse — agora só com contagem + busca + botão */}
        <div style={{
          padding: '14px 16px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: COR.muted, flex: 1 }}>
            {usuariosFiltrados.length} registro{usuariosFiltrados.length !== 1 ? 's' : ''}
          </span>

          {/* Busca */}
          <input
            type="text"
            placeholder="Pesquisar..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            style={{ ...inputStyle, width: '320px' }}
          />

          {/* Botão Incluir */}
          <SePode rotina="usuarios" acao="incluir">
            <BtnIcone icone="incluir"
              onClick={abrirNovo}
              title="Incluir novo usuário"
              cor={COR.azul}
              solido tamanho="grande" />
          </SePode>
        </div>

        {/* Tabela */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: COR.azul }}>
                {/* r106: 1ª coluna fixa pro ícone SVG da rotina */}
                <th style={{ padding: '10px 14px', width: '36px' }}></th>
                {[
                  { label: 'Nome',            campo: 'nome'         },
                  { label: 'Nome amigável',   campo: 'nome_amigavel'},
                  { label: 'E-mail',          campo: 'email'        },
                  { label: 'Grupo de acesso', campo: 'grupo_desc'   },
                  { label: 'Status',          campo: 'status'       },
                  { label: 'Ações',           campo: null           },
                ].map(({ label, campo }) => (
                  <th
                    key={label}
                    onClick={() => campo && toggleOrdem(campo)}
                    style={{
                      padding: '10px 14px', textAlign: 'left',
                      fontSize: '13px', fontWeight: '700',
                      color: COR.branco, whiteSpace: 'nowrap',
                      cursor: campo ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}
                  >
                    {label}{campo && setaCabecalho(campo)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: COR.muted }}>Carregando...</td></tr>
              ) : usuariosFiltrados.length === 0 ? (
                <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: COR.muted }}>Nenhum usuário encontrado.</td></tr>
              ) : (
                usuariosFiltrados.map((u, i) => (
                  <tr
                    key={u.id}
                    style={{
                      borderBottom: `1px solid ${COR.borda}`,
                      backgroundColor: selecionado?.id === u.id ? COR.azulBg : i % 2 === 0 ? COR.branco : '#fafafa',
                      cursor: 'pointer',
                    }}
                    onClick={() => abrirVisualizar(u)}
                  >
                    {/* r106: ícone SVG #003399 da rotina (mesmo do menu lateral) */}
                    <td style={{ padding: '10px 0 10px 14px', verticalAlign: 'middle' }}>
                      {iconeDaRotina('usuarios', { width: 20, height: 20, color: COR.azul })}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: '500', color: COR.texto }}>{u.nome}</td>
                    <td style={{ padding: '10px 14px', color: COR.muted }}>{u.nome_amigavel}</td>
                    <td style={{ padding: '10px 14px', color: COR.muted }}>{u.email}</td>
                    <td style={{ padding: '10px 14px', color: COR.muted }}>{u.grupo_desc}</td>
                    <td style={{ padding: '10px 14px' }}><StatusPill status={u.status} /></td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                        <SePode rotina="usuarios" acao="alterar">
                          <BtnIcone icone="alterar"    onClick={() => abrirEditar(u)}    title="Alterar" cor={COR.azul} />
                        </SePode>
                        {/* r159: bloquear/desbloquear e resetar senha são ações administrativas */}
                        {podeMexerEmCamposAdmin && (
                          <>
                            <SePode rotina="usuarios" acao="alterar">
                              <BtnIcone
                                icone={u.status === 'A' ? 'bloquear' : 'desbloquear'}
                                onClick={() => alterarStatus(u)}
                                title={u.status === 'A' ? 'Bloquear' : 'Desbloquear'}
                                cor={u.status === 'A' ? COR.vermelho : COR.verde}
                              />
                            </SePode>
                            <SePode rotina="usuarios" acao="alterar">
                              <BtnIcone icone="senha" onClick={() => resetarSenha(u)} title="Resetar senha" cor={COR.amarelo} />
                            </SePode>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MODAL ── */}
      <Modal
        aberto={painelAberto}
        titulo={painel === 'novo' ? 'Novo Usuário' : painel === 'editar' ? 'Alterar Usuário' : 'Visualizar Usuário'}
        onFechar={fecharPainel}
        onSalvar={salvar}
        salvando={salvando}
        somenteLeitura={somenteLeitura}
        onEditar={() => abrirEditar(selecionado)}
        // r112: item 6 — header rico com ícone SVG da rotina, descrição e botões no topo
        rotina="usuarios"
        descricao="Cadastro dos usuários com acesso ao sistema."
        botoesNoTopo={true}
        // r112: item 2 — largura aumentada
        largura="95vw"
      >
        <div>

            {/* Alertas */}
            {erroPainel && (
              <div style={{ backgroundColor: COR.vermBg, border: `1px solid #ffcccc`, borderRadius: '5px', color: COR.vermelho, fontSize: '13px', padding: '10px 12px', marginBottom: '14px' }}>
                {erroPainel}
              </div>
            )}
            {sucessoPainel && (
              <div style={{ backgroundColor: COR.verdeBg, border: `1px solid #a8e6c1`, borderRadius: '5px', color: '#1d6a3a', fontSize: '13px', padding: '10px 12px', marginBottom: '14px' }}>
                {sucessoPainel}
              </div>
            )}

            {/* Dados cadastrais */}
            <div style={{ fontSize: '11px', fontWeight: '700', color: COR.azul, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '12px' }}>
              Dados Cadastrais
            </div>

            <Campo label="Nome completo" obrigatorio>
              <input name="nome" value={form.nome} onChange={handleCampo} disabled={somenteLeitura} style={inputStyle} placeholder="Nome completo" />
            </Campo>

            {/* r113: Nome amigável + Grupo de acesso na mesma linha */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <Campo label="Nome amigável" obrigatorio>
                <input name="nome_amigavel" value={form.nome_amigavel} onChange={handleCampo} disabled={somenteLeitura} style={inputStyle} placeholder="Como será chamado no sistema" />
              </Campo>
              <Campo label="Grupo de acesso" obrigatorio>
                <select name="grupo_acesso_id" value={form.grupo_acesso_id}
                        onChange={handleCampo}
                        disabled={somenteLeitura || !podeDefinirGrupo}
                        title={!podeDefinirGrupo ? 'Você não tem permissão para definir o grupo de acesso' : undefined}
                        style={selectStyle}>
                  <option value="">Selecione...</option>
                  {grupos.map(g => (
                    <option key={g.id} value={g.id}>{g.descricao}</option>
                  ))}
                </select>
              </Campo>
            </div>

            <Campo label="E-mail" obrigatorio>
              <input name="email" type="email" value={form.email} onChange={handleCampo} disabled={somenteLeitura} style={inputStyle} placeholder="email@exemplo.com.br" />
            </Campo>

            {/* r113: CPF + Nascimento + Sexo + Status na mesma linha (4 colunas) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr 1fr', gap: '10px' }}>
              <Campo label="CPF">
                {/* r112: máscara 000.000.000-00. Armazena só números no form. */}
                <input
                  name="cpf"
                  value={formatarCpf(form.cpf)}
                  onChange={e => setForm(f => ({ ...f, cpf: limparCpf(e.target.value) }))}
                  disabled={somenteLeitura}
                  style={inputStyle}
                  placeholder="000.000.000-00"
                  maxLength={14}
                  inputMode="numeric"
                />
              </Campo>
              <Campo label="Nascimento">
                <input name="nascimento" type="date" value={dataParaInput(form.nascimento)} onChange={handleCampo} disabled={somenteLeitura} style={inputStyle} />
              </Campo>
              <Campo label="Sexo" obrigatorio>
                <select name="sexo" value={form.sexo} onChange={handleCampo} disabled={somenteLeitura} style={selectStyle}>
                  <option value="M">Masculino</option>
                  <option value="F">Feminino</option>
                </select>
              </Campo>
              <Campo label="Status" obrigatorio>
                <select name="status" value={form.status}
                        onChange={handleCampo}
                        disabled={somenteLeitura || !podeMexerEmCamposAdmin}
                        title={!podeMexerEmCamposAdmin ? 'Apenas administradores podem alterar o status' : undefined}
                        style={selectStyle}>
                  <option value="A">Ativo</option>
                  <option value="B">Bloqueado</option>
                </select>
              </Campo>
            </div>

            {/* Senha — só em inclusão ou edição */}
            {!somenteLeitura && (
              <>
                <div style={{ fontSize: '11px', fontWeight: '700', color: COR.azul, textTransform: 'uppercase', letterSpacing: '0.6px', margin: '16px 0 12px' }}>
                  {painel === 'novo' ? 'Senha' : 'Alterar Senha (opcional)'}
                </div>
                {/* r113: Senha + Confirmar senha na mesma linha */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <Campo label="Senha" obrigatorio={painel === 'novo'}>
                    <input name="senha" type="password" value={form.senha} onChange={handleCampo} style={inputStyle} placeholder="Mínimo 8 caracteres" />
                  </Campo>
                  <Campo label="Confirmar senha" obrigatorio={painel === 'novo'}>
                    <input name="conf_senha" type="password" value={form.conf_senha} onChange={handleCampo} style={inputStyle} placeholder="Repita a senha" />
                  </Campo>
                </div>
              </>
            )}

            {/* r116 item 1: Foto agora no final do form */}
            <FotoUsuario
              fotoUrl={form.foto}
              fotoPendente={fotoPendente}
              nomeAmigavel={form.nome_amigavel}
              onEscolher={escolherFoto}
              onRemover={removerFoto}
              onAjuste={setFotoAjuste}
              ajuste={fotoAjuste}
              zoom={fotoZoom}
              onZoom={setFotoZoom}
              somenteLeitura={somenteLeitura}
            />
        </div>
      </Modal>
    </div>
  );
}

export default UsuariosPage;
